import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell from "@/components/layout/AuthShell";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/sign-in", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Set a new password"
      footer={<><Link to="/sign-in" className="font-semibold text-ff-red hover:underline">Back to sign in</Link></>}
    >
      {!token ? (
        <Alert variant="destructive" data-testid="reset-no-token">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>This reset link is missing a token. Request a new one.</AlertDescription>
        </Alert>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          {error && (
            <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>
          )}
          <div>
            <Label htmlFor="password">New password</Label>
            <Input id="password" type="password" required data-testid="reset-password-input" value={password}
              onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label htmlFor="confirm">Confirm password</Label>
            <Input id="confirm" type="password" required data-testid="reset-confirm-input" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading} data-testid="reset-submit-button"
            className="w-full bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating</> : "Update Password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
